<template>
    <div class="counter-card-form">
        <div class="w-full">
            <RedesignFormItem
                :label="typeLabel"
            >
                <RedesignBaseSelect
                    v-model="counterTypeModel"
                    :multiple="false"
                    :data="availableTypesOptions"
                    filterable
                    full-width
                    @change="onStatusChange"
                />
            </RedesignFormItem>
            <RedesignFormItem
                :label="$t('widget.contentTitle')"
                :prop="`WidgetLayout.ContentTitle.${counterTypeModel}`"
                :rules="[{ validator: triggerValidateRule, trigger: 'blur' }]"
            >
                <RedesignBaseInput
                    v-model="contentTitleModel"
                />
            </RedesignFormItem>
        </div>
        <div class="mb-5 space-y-1.5">
            <RedesignCheckbox
                v-model="widgetLayoutModel.showText"
            >
                {{ $t('status.show.text') }}
            </RedesignCheckbox>
            <RedesignCheckbox
                v-model="widgetLayoutModel.displayBorder"
            >
                {{ $t('status.display.border') }}
            </RedesignCheckbox>
        </div>
        <div class="mb-5">
            <WidgetInnerAlignment
                :content="widgetContentAlignment"
                :layout="widgetContentLayout"
                @update:content="onUpdateContentAlignment"
                @update:layout="onUpdateContentLayout"
            />
        </div>
        <div class="mb-5">
            <WidgetCardLayoutConfigs v-model="widgetLayoutModel" />
        </div>
        <div class="mb-5">
            <BaseCollapseItem
                :title="$t('settings.sounds')"
                name="sounds"
                class="simple-collapse"
            >
                <WidgetStatusCardSounds v-model="widgetLayoutModel" />
            </BaseCollapseItem>
        </div>
        <div
            v-if="widgetConfigData.filters"
            class="mb-5"
        >
            <BaseCollapseItem
                :title="$t('settings.filters')"
                name="filters"
                class="simple-collapse"
            >
                <div>
                    <div
                        v-for="(filter, index) in widgetConfigData.filters"
                        :key="`filter-${index}`"
                    >
                        <redesign-widget-config-entity-tree
                            v-if="isAutoComplete(filter)"
                            :key="`filter-auto-complete-${filter.ParameterID}`"
                            :value="getWidgetConfig(filter)"
                            class="mb-4"
                            @update:value="updateFilterItem"
                        />
                        <RedesignWidgetConfigSelect
                            v-if="isMultiSelect(filter)"
                            :key="`filter-select-${filter.ParameterID}`"
                            :value="getWidgetConfig(filter)"
                            @update:value="updateFilterItem"
                        />
                    </div>
                </div>
            </BaseCollapseItem>
        </div>
    </div>
</template>

<script setup lang="ts">
import RedesignFormItem from '@/components/RedesignBaseComponents/Form/RedesignFormItem.vue'
import RedesignBaseSelect from '@/components/RedesignBaseComponents/Inputs/RedesignBaseSelect.vue'
import { WidgetConfig, WidgetLayout } from '@/types/dashboard'
import { useVModel } from '@vueuse/core'
import { computed, onMounted } from 'vue'
import RedesignBaseInput from '@/components/RedesignBaseComponents/Inputs/RedesignBaseInput.vue'
import i18n from '@/i18n'
import RedesignCheckbox from '@/components/RedesignBaseComponents/Inputs/RedesignCheckbox.vue'
import BaseCollapseItem from '@/components/RedesignBaseComponents/Collapse/BaseCollapseItem.vue'
import {
    getWidgetConfigByTemplateId,
    getWidgetConfigMapped,
    isAutoComplete,
    isMultiSelect
} from '@/components/RedesignComponents/Widgets/widget.helper'
import RedesignWidgetConfigSelect from '@/components/RedesignComponents/Widgets/Common/RedesignWidgetConfigSelect.vue'
import WidgetStatusCardSounds from '@/components/RedesignComponents/Widgets/Common/WidgetStatusCardSounds.vue'
import WidgetCardLayoutConfigs
    from '@/components/RedesignComponents/Widgets/Edit/WidgetCardsForms/WidgetCardLayoutConfigs.vue'
import get from 'lodash/get'
import WidgetInnerAlignment from '@/components/RedesignComponents/Widgets/Common/WidgetInnerAlignment.vue'
import RedesignWidgetConfigEntityTree
    from '@/components/RedesignComponents/Widgets/Common/RedesignWidgetConfigEntityTree.vue'

interface TypeOption {
    value: number
    label: string
}

interface TypeConfig {
    id: number
    text: string
    icon?: string
    color?: string
    type?: string
}

interface IProps {
    value: WidgetLayout
    widgetConfig: WidgetConfig[]
    templateId: number
    /** Property name in WidgetLayout to store the counter type (e.g., 'queueType', 'voicebotType') */
    typePropertyName: string
    /** Translation key for the type label (e.g., 'queue.counter.type', 'voicebot.counter.type') */
    typeLabelKey: string
    /** Available type options for the select */
    typeOptions: TypeOption[]
    /** Types configuration object mapping id to type config */
    typesConfig: Record<number, TypeConfig>
}

const props = defineProps<IProps>()
const emit = defineEmits<{
    (e: 'input', pl: WidgetLayout): void
    (e: 'update:config', pl: WidgetConfig): void
}>()

const widgetLayoutModel = useVModel(props, 'value', emit, {
    eventName: 'input',
    passive: true
})

const typeLabel = computed(() => {
    return i18n.t(props.typeLabelKey).toString()
})

const defaultTitle = computed(() => {
    if (!props.typesConfig[counterTypeModel.value]) {
        return ''
    }
    return i18n.t(props.typesConfig[counterTypeModel.value].text)
})

const counterTypeModel = computed({
    get () {
        return (widgetLayoutModel.value as Record<string, unknown>)[props.typePropertyName] as number || 0
    },
    set (val) {
        widgetLayoutModel.value = {
            ...widgetLayoutModel.value,
            [props.typePropertyName]: val
        }
    }
})

const contentTitleModel = computed({
    get () {
        return (widgetLayoutModel.value?.ContentTitle?.[counterTypeModel.value] ||
            widgetLayoutModel.value?.ContentTitle?.[counterTypeModel.value] === ''
            ? widgetLayoutModel.value?.ContentTitle[counterTypeModel.value]
            : defaultTitle.value) as string
    },
    set (val) {
        if (counterTypeModel.value === undefined || defaultTitle.value === val) {
            return
        }
        const ContentTitle = widgetLayoutModel.value.ContentTitle || {}
        widgetLayoutModel.value = {
            ...widgetLayoutModel.value,
            ContentTitle: {
                ...ContentTitle,
                [counterTypeModel.value]: String(val)
            }
        }
    }
})

const widgetContentAlignment = computed(() => {
    return widgetLayoutModel.value?.contentAlignment ?? 'center'
})

const widgetContentLayout = computed(() => {
    return widgetLayoutModel.value?.contentLayout ?? 'default'
})

function onUpdateContentAlignment (val: string) {
    widgetLayoutModel.value = {
        ...widgetLayoutModel.value,
        contentAlignment: val
    }
}

function onUpdateContentLayout (val: string) {
    widgetLayoutModel.value = {
        ...widgetLayoutModel.value,
        contentLayout: val
    }
}

const availableTypesOptions = computed(() => {
    return props.typeOptions
})

const widgetConfigData = computed(() => {
    return getWidgetConfigMapped(props.widgetConfig)
})

function onStatusChange (status: number) {
    const title = get(widgetLayoutModel.value, `ContentTitle[${status}]`, '')
    if (!title) {
        contentTitleModel.value = String(i18n.t(props.typesConfig[status].text))
    }
}

function getWidgetConfig (config: WidgetConfig) {
    return getWidgetConfigByTemplateId(config, props.templateId)
}

function updateFilterItem (filterData: WidgetConfig) {
    emit('update:config', filterData)
}

const triggerValidateRule = (_rule: unknown, _value: unknown, cb: (error?: string) => void) => {
    const realValue = widgetLayoutModel.value?.ContentTitle?.[counterTypeModel.value]
    if (realValue === '') {
        cb(i18n.t('validation.required') as string)
    } else cb()
}

onMounted(() => {
    if (!contentTitleModel.value) {
        contentTitleModel.value = String(i18n.t(props.typesConfig[counterTypeModel.value].text))
    }
})
</script>

